/**
 * «Размещение»: формы и справка в боковых ящиках — внести строку, исправить, изменение на земле,
 * импорт файла, севооборот контура. На странице они не раскрываются лентой: страница остаётся
 * таблицей, а ввод открывается рядом с ней и закрывается, не теряя места в таблице.
 *
 * Все контролы — из `astra/ui/forms`. Экран ничего не решает: права, отказы и «нельзя
 * удалить» приходят с сервера и показываются словами сервера.
 */

import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, ApiError, type QueryParams } from "../../api/client";
import { useRequest } from "../../api/useRequest";
import { useAnalysisContext } from "../../analysis/context";
import { DetailDrawer, DrawerFormActions } from "../../astra/ui/DetailDrawer";
import { clampToSlice, useSliceDate } from "../../analysis/useSliceDate";
import { EmptyState } from "../../astra/ui/EmptyState";
import { LoadError, failed } from "../../astra/ui/LoadError";
import { Badge } from "../../astra/ui/Badge";
import { SkeletonList } from "../../astra/ui/Skeleton";
import { Checkbox, DatePicker, FormActions, NumberField, Select, todayIso, toast, type Choice } from "../../astra/ui/forms";
import { FilePick } from "../../astra/pages/ops/FilePick";
import { COMMON } from "../../i18n/common";
import { defineMessages } from "../../i18n/define";
import { useT } from "../../i18n/hooks";
import { cropName } from "../../lib/crops";
import {
  placementApi,
  type ImportDetail,
  type PlacementIn,
  type PlacementRow,
} from "./placementApi";
import { DemoCropTimeline } from "./DemoCropTimeline";
import {
  COMMITTABLE,
  IMPORT_STATUS_ORDER,
  MICROHA,
  fieldLabel,
  formatHa,
  importStatusLabel,
} from "./placementModel";

const m = defineMessages({
  // --- форма строки
  errArea: { id: "placement.form.errArea", defaultMessage: "Площадь — число гектаров больше нуля, например 7,5.", description: "Ошибка формы размещения (менеджер): площадь не введена или не число; пример с десятичной запятой" },
  errNeedContour: { id: "placement.form.errNeedContour", defaultMessage: "Нужны контур из реестра и культура.", description: "Ошибка формы размещения: не выбран контур из реестра или не указана культура" },
  titleCreate: { id: "placement.form.titleCreate", defaultMessage: "Внести строку размещения", description: "Заголовок формы: внести новую строку размещения (какое поле под какой культурой)" },
  titleCorrection: { id: "placement.form.titleCorrection", defaultMessage: "Исправить ошибку ввода — {field}", description: "Заголовок формы исправления строки размещения; {field} — номер контура (и часть)" },
  titleChange: { id: "placement.form.titleChange", defaultMessage: "Изменение на земле — {field}", description: "Заголовок формы изменения на земле (пересев, передача поля); {field} — номер контура (и часть)" },
  hintCorrection: { id: "placement.form.hintCorrection", defaultMessage: "Исправление: прежняя строка вытесняется, дата «с» сохраняется — на земле ничего не менялось.", description: "Пояснение в форме исправления строки размещения" },
  hintChange: { id: "placement.form.hintChange", defaultMessage: "Изменение на земле (пересев, передача поля): прежняя строка закрывается этой датой, новая открывается с неё.", description: "Пояснение в форме изменения на земле" },
  fieldSeason: { id: "placement.form.fieldSeason", defaultMessage: "Сезон (год посадки)", description: "Подпись поля формы размещения: сезон — год посадки" },
  fieldCropPick: { id: "placement.form.fieldCropPick", defaultMessage: "Культура", description: "Подпись выпадающего списка культуры в форме размещения" },
  fieldContour: { id: "placement.form.fieldContour", defaultMessage: "Контур", description: "Подпись поля формы размещения: контур из реестра" },
  placeholderContourSearch: { id: "placement.form.placeholderContourSearch", defaultMessage: "Найти по номеру контура", description: "Подсказка внутри поля поиска контура (не меньше двух цифр); коротко" },
  contourEmpty: { id: "placement.form.contourEmpty", defaultMessage: "Введите не меньше двух знаков номера", description: "Пустой список поиска контура: нужно ввести хотя бы два знака номера" },
  contourNotFound: { id: "placement.form.contourNotFound", defaultMessage: "Контура с таким номером нет", description: "Пустой список поиска контура: по введённому номеру ничего не найдено" },
  fieldPart: { id: "placement.form.fieldPart", defaultMessage: "Часть контура", description: "Подпись выпадающего списка: часть нарезанного контура" },
  wholeContour: { id: "placement.form.wholeContour", defaultMessage: "весь контур", description: "Вариант списка частей контура: контур целиком; строчная" },
  fieldFarm: { id: "placement.form.fieldFarm", defaultMessage: "Хозяйство", description: "Подпись поля хозяйства в форме размещения: выбор хозяйства из реестра" },
  noFarmOption: { id: "placement.form.noFarmOption", defaultMessage: "без хозяйства (не сопоставлено)", description: "Вариант списка хозяйств в форме размещения: хозяйство не указано; строчная" },
  placeholderFarmSearch: { id: "placement.form.placeholderFarmSearch", defaultMessage: "Найти по названию хозяйства", description: "Подсказка внутри поля поиска хозяйства; коротко" },
  farmEmpty: { id: "placement.form.farmEmpty", defaultMessage: "Введите не меньше двух букв названия", description: "Пустой список поиска хозяйства: нужно ввести хотя бы две буквы названия" },
  farmNotFound: { id: "placement.form.farmNotFound", defaultMessage: "Хозяйства с таким названием нет", description: "Пустой список поиска хозяйства: по введённому названию ничего не найдено" },
  fieldArea: { id: "placement.form.fieldArea", defaultMessage: "Площадь", description: "Подпись поля формы размещения: площадь; единица «га» стоит внутри поля" },
  haUnit: { id: "placement.form.haUnit", defaultMessage: "га", description: "Единица площади внутри поля ввода: гектары; коротко" },
  fieldFrom: { id: "placement.form.fieldFrom", defaultMessage: "С даты", description: "Подпись поля формы изменения на земле: дата, с которой действует новая строка" },
  endOnly: { id: "placement.form.endOnly", defaultMessage: "культура с поля ушла — новой строки нет", description: "Флажок в форме изменения на земле: поле освободилось, новую строку не создавать" },
  save: { id: "placement.form.save", defaultMessage: "Сохранить", description: "Кнопка формы размещения: сохранить" },
  pickContour: { id: "placement.form.pickContour", defaultMessage: "{no} · {massif}", description: "Вариант в списке подсказок поиска контура: номер контура и массив; {massif} — имя массива" },
  ctxFarm: { id: "placement.form.ctxFarm", defaultMessage: "Хозяйство", description: "Подпись строки сводки в форме исправления: хозяйство строки" },
  ctxCrop: { id: "placement.form.ctxCrop", defaultMessage: "Культура", description: "Подпись строки сводки в форме исправления: культура строки" },
  ctxArea: { id: "placement.form.ctxArea", defaultMessage: "Площадь, га", description: "Подпись строки сводки в форме исправления: площадь строки в гектарах" },
  saved: { id: "placement.form.saved", defaultMessage: "Строка размещения сохранена", description: "Уведомление после сохранения строки размещения" },
  // --- импорт
  importDone: { id: "placement.import.done", defaultMessage: "Внесено строк: {done}; отказ при фиксации: {failed}.", description: "Итог фиксации файла размещения: {done} — внесено, {failed} — отказов; числа" },
  importTitle: { id: "placement.import.title", defaultMessage: "Импорт размещения из файла", description: "Заголовок панели импорта файла размещения (менеджер)" },
  importHint: {
    id: "placement.import.hint",
    defaultMessage: "Канонический формат <code>placement_import.v1</code> (CSV). Сначала предпросмотр — в корпус ничего не пишется; потом фиксация. Тот же файл второй раз не внесётся; исправленный ляжет поверх без дублей.",
    description: "Пояснение в панели импорта: формат файла и порядок (предпросмотр → фиксация); <code> — имя формата, не переводить; «корпус» — набор принятых данных",
  },
  importFile: { id: "placement.import.file", defaultMessage: "Файл", description: "Подпись поля выбора файла импорта" },
  importFormat: { id: "placement.import.format", defaultMessage: "Формат", description: "Подпись выпадающего списка формата файла импорта" },
  formatCanon: { id: "placement.import.formatCanon", defaultMessage: "канон", description: "Вариант формата файла импорта: канонический формат; строчная" },
  formatTable: { id: "placement.import.formatTable", defaultMessage: "таблица с русскими заголовками", description: "Вариант формата файла импорта: таблица CSV с заголовками на русском; строчная" },
  importSeason: { id: "placement.import.season", defaultMessage: "Сезон файла (если не указан в строках)", description: "Подпись поля сезона файла импорта" },
  importPreview: { id: "placement.import.preview", defaultMessage: "Предпросмотр", description: "Кнопка панели импорта: показать, что будет внесено, ничего не записывая" },
  countLine: { id: "placement.import.countLine", defaultMessage: "{label}: <b>{count}</b>", description: "Строка счётчика предпросмотра: статус строк файла и их число; {label} — статус («будет внесена»), {count} — число" },
  farmUnmatched: { id: "placement.import.farmUnmatched", defaultMessage: "Без хозяйства (не сопоставлено): {count} — земля войдёт в цикл, участников не добавит.", description: "Пояснение к предпросмотру импорта: строки без сопоставленного хозяйства; {count} — число" },
  problemsTitle: { id: "placement.import.problemsTitle", defaultMessage: "Строки, которые не внесутся", description: "Заголовок списка проблемных строк файла в предпросмотре импорта" },
  problemRow: { id: "placement.import.problemRow", defaultMessage: "Строка {row} · контур {contour}", description: "Заголовок проблемной строки файла; {row} — номер строки файла, {contour} — контур как записан в файле" },
  problemsMore: { id: "placement.import.problemsMore", defaultMessage: "и ещё {count}", description: "Хвост списка проблемных строк файла: сколько ещё не показано; {count} — число" },
  commitRows: {
    id: "placement.import.commitRows",
    defaultMessage: "Внести {count, plural, one {# строку} few {# строки} many {# строк} other {# строки}}",
    description: "Кнопка фиксации импорта: сколько строк будет внесено; # — число",
  },
  commitNote: { id: "placement.import.commitNote", defaultMessage: "Вносить нечего: в файле нет новых и исправляющих строк.", description: "Причина неактивной кнопки фиксации импорта: ни одной строки к внесению" },
  rejectFile: { id: "placement.import.rejectFile", defaultMessage: "Отклонить файл", description: "Кнопка панели импорта: отклонить загруженный файл целиком" },
  // --- севооборот
  timelineTitle: { id: "placement.timeline.title", defaultMessage: "Севооборот контура {no}", description: "Заголовок ящика с цепочкой культур контура по времени; {no} — номер контура" },
  timelineLoadWhat: { id: "placement.timeline.loadWhat", defaultMessage: "севооборот контура", description: "Что не загрузилось, в винительном падеже, для сообщения «Не удалось загрузить …»; также подпись скелетона" },
  timelineEmptyTitle: { id: "placement.timeline.emptyTitle", defaultMessage: "Севооборот не задан", description: "Заголовок пустого состояния ящика севооборота контура" },
  timelineEmptyReason: { id: "placement.timeline.emptyReason", defaultMessage: "по контуру нет цепочки культур на дату среза; демо-данные показываются, когда включён флаг «демо»", description: "Пояснение пустого состояния ящика севооборота; строчными, после заголовка" },
  timelineCard: { id: "placement.timeline.card", defaultMessage: "Открыть карточку контура", description: "Ссылка в ящике севооборота: открыть полную карточку контура" },
});

export type RowFormState =
  | { kind: "create" }
  | { kind: "correction"; row: PlacementRow }
  | { kind: "change"; row: PlacementRow };

const DEFAULT_CROPS = ["COTTON", "GRAIN", "RICE", "OTHER_CROPS"];

function errorText(cause: unknown): string {
  return cause instanceof ApiError ? cause.message : String(cause);
}

/** Поиск справочника с задержкой: от двух знаков, устаревший ответ отбрасывается. */
function useLookup<T>(query: string, load: (needle: string) => Promise<T[]>) {
  const [state, setState] = useState<{ rows: T[]; loading: boolean }>({ rows: [], loading: false });
  useEffect(() => {
    const needle = query.trim();
    if (needle.length < 2) {
      setState({ rows: [], loading: false });
      return undefined;
    }
    let alive = true;
    setState((prev) => ({ ...prev, loading: true }));
    const timer = window.setTimeout(() => {
      load(needle).then(
        (found) => alive && setState({ rows: found, loading: false }),
        () => alive && setState({ rows: [], loading: false }),
      );
    }, 250);
    return () => {
      alive = false;
      window.clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);
  return state;
}

/* ------------------------------------------------------------------ строка */

export function RowFormDrawer({
  form,
  season,
  seasons,
  cropCodes,
  onDone,
  onClose,
}: {
  form: RowFormState;
  season: string;
  seasons: readonly string[];
  /** Культуры, которые уже есть в сезоне: к ним добавляются основные коды. */
  cropCodes: readonly { code: string; name: string }[];
  onDone: () => void;
  onClose: () => void;
}) {
  const t = useT();
  const analysis = useAnalysisContext();
  const base = form.kind === "create" ? null : form.row;
  const fixedField = form.kind !== "create";

  const [seasonValue, setSeasonValue] = useState(season || String(new Date().getFullYear()));
  const [crop, setCrop] = useState(base?.crop_code ?? "");
  const [contour, setContour] = useState<{ id: number; no: string } | null>(base ? { id: base.contour_id, no: base.contour_no } : null);
  const [contourQuery, setContourQuery] = useState("");
  const [partId, setPartId] = useState<string>(base?.part_id ? String(base.part_id) : "");
  const [parts, setParts] = useState<{ id: number; label: string }[]>([]);
  const [actor, setActor] = useState<{ id: number; name: string } | null>(base?.actor_id ? { id: base.actor_id, name: base.actor_name ?? `#${base.actor_id}` } : null);
  const [farmQuery, setFarmQuery] = useState("");
  const [area, setArea] = useState<number | null>(base?.area_microha ? base.area_microha / MICROHA : null);
  // `undefined` — дату не трогали: по умолчанию «сегодня по часам, но не позже среза».
  const slice = useSliceDate();
  const [effectiveOn, setEffectiveOn] = useState<string | null | undefined>(undefined);
  const effectiveDay = effectiveOn === undefined ? clampToSlice(slice) : effectiveOn;
  const [endOnly, setEndOnly] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const contours = useLookup(contourQuery, async (needle) => (await api.contours({ ...analysis.params, search: needle, limit: 20 })).items);
  const farms = useLookup(farmQuery, async (needle) => (await api.actors({ ...analysis.params, search: needle, limit: 20 })).items);

  useEffect(() => {
    if (!contour || fixedField) {
      setParts([]);
      return;
    }
    api.contour(contour.id, analysis.params).then(
      (detail) => setParts(detail.parts.map((p) => ({ id: p.id, label: p.label }))),
      () => setParts([]),
    );
  }, [contour, fixedField, analysis.params]);

  const seasonOptions = useMemo<Choice[]>(() => {
    const year = new Date().getFullYear();
    const all = new Set([...seasons, seasonValue, String(year), String(year + 1)]);
    return [...all].sort().reverse().map((s) => ({ id: s, name: s }));
  }, [seasons, seasonValue]);

  const cropOptions = useMemo<Choice[]>(() => {
    const names = new Map(cropCodes.map((c) => [c.code, c.name]));
    for (const code of DEFAULT_CROPS) if (!names.has(code)) names.set(code, "");
    if (crop && !names.has(crop)) names.set(crop, "");
    return [...names].map(([code, name]) => ({ id: code, name: cropName(code, name || null) }));
  }, [cropCodes, crop]);

  const contourOptions = useMemo<Choice[]>(
    () => contours.rows.map((c) => ({ id: String(c.id), name: t(m.pickContour, { no: c.contour_no, massif: c.massif.canonical_name }) })),
    [contours.rows, t],
  );
  const farmOptions = useMemo<Choice[]>(
    () => [{ id: "", name: t(m.noFarmOption) }, ...farms.rows.map((f) => ({ id: String(f.id), name: f.name ?? `#${f.id}` }))],
    [farms.rows, t],
  );
  const partOptions = useMemo<Choice[]>(() => [{ id: "", name: t(m.wholeContour) }, ...parts.map((p) => ({ id: String(p.id), name: p.label }))], [parts, t]);

  // Несохранённый ввод: ящик не закрывается Escape и кликом вне без вопроса.
  const dirty =
    seasonValue !== (season || String(new Date().getFullYear())) ||
    crop !== (base?.crop_code ?? "") ||
    (contour?.id ?? null) !== (base?.contour_id ?? null) ||
    partId !== (base?.part_id ? String(base.part_id) : "") ||
    (actor?.id ?? null) !== (base?.actor_id ?? null) ||
    area !== (base?.area_microha ? base.area_microha / MICROHA : null) ||
    effectiveOn !== undefined ||
    endOnly;

  const microha = area !== null && area > 0 ? Math.round(area * MICROHA) : null;
  const needArea = !endOnly;
  const problem =
    form.kind === "create" && (!contour || !crop)
      ? t(m.errNeedContour)
      : needArea && microha === null
        ? t(m.errArea)
        : null;

  async function submit() {
    if (problem) return;
    setError(null);
    setBusy(true);
    try {
      if (form.kind === "create") {
        const body: PlacementIn = {
          season: seasonValue.trim(),
          crop,
          contour_id: contour!.id,
          contour_part_id: partId ? Number(partId) : null,
          area_microha: microha!,
          actor_id: actor?.id ?? null,
        };
        await placementApi.create(body, analysis.params);
      } else if (form.kind === "correction") {
        await placementApi.patch(
          form.row.id,
          {
            mode: "correction",
            end_only: false,
            clear_actor: actor === null && form.row.actor_id !== null,
            area_microha: microha,
            ...(actor !== null && actor.id !== form.row.actor_id ? { actor_id: actor.id } : {}),
          },
          analysis.params,
        );
      } else {
        await placementApi.patch(
          form.row.id,
          {
            mode: "change",
            clear_actor: false,
            effective_on: effectiveDay ?? todayIso(),
            end_only: endOnly,
            ...(endOnly ? {} : { area_microha: microha }),
            ...(!endOnly && actor !== null && actor.id !== form.row.actor_id ? { actor_id: actor.id } : {}),
          },
          analysis.params,
        );
      }
      toast.show({ message: t(m.saved) });
      onDone();
    } catch (cause) {
      setError(errorText(cause));
    } finally {
      setBusy(false);
    }
  }

  const title =
    form.kind === "create"
      ? t(m.titleCreate)
      : form.kind === "correction"
        ? t(m.titleCorrection, { field: fieldLabel(form.row) })
        : t(m.titleChange, { field: fieldLabel(form.row) });

  return (
    <DetailDrawer title={title} onClose={onClose} isDirty={dirty}>
      <div className="pl-form">
        {form.kind === "correction" ? <p className="pl-hint">{t(m.hintCorrection)}</p> : null}
        {form.kind === "change" ? <p className="pl-hint">{t(m.hintChange)}</p> : null}

        {base ? (
          <dl className="pl-ctx">
            <dt>{t(m.ctxFarm)}</dt>
            <dd>{base.actor_name ?? "—"}</dd>
            <dt>{t(m.ctxCrop)}</dt>
            <dd>{cropName(base.crop_code, base.crop_name)}</dd>
            <dt>{t(m.ctxArea)}</dt>
            <dd>{formatHa(base.area_microha)}</dd>
          </dl>
        ) : null}

        {form.kind === "create" ? (
          <>
            <Select label={t(m.fieldSeason)} value={seasonValue} onChange={(v) => setSeasonValue(v)} options={seasonOptions} />
            <Select label={t(m.fieldCropPick)} value={crop} onChange={(v) => setCrop(v)} options={cropOptions} />
            <Select
              label={t(m.fieldContour)}
              value={contour ? String(contour.id) : ""}
              valueLabel={contour?.no}
              placeholder={t(m.placeholderContourSearch)}
              options={contourOptions}
              onSearch={setContourQuery}
              isLoading={contours.loading}
              emptyText={contourQuery.trim().length < 2 ? t(m.contourEmpty) : t(m.contourNotFound)}
              onChange={(id) => {
                const found = contours.rows.find((c) => String(c.id) === id);
                setContour(found ? { id: found.id, no: found.contour_no } : null);
                setPartId("");
              }}
            />
            {parts.length > 0 ? <Select label={t(m.fieldPart)} value={partId} onChange={(v) => setPartId(v)} options={partOptions} /> : null}
          </>
        ) : null}

        <Select
          label={t(m.fieldFarm)}
          value={actor ? String(actor.id) : ""}
          valueLabel={actor?.name}
          placeholder={t(m.placeholderFarmSearch)}
          options={farmOptions}
          onSearch={setFarmQuery}
          isLoading={farms.loading}
          emptyText={farmQuery.trim().length < 2 ? t(m.farmEmpty) : t(m.farmNotFound)}
          onChange={(id) => {
            if (id === "") {
              setActor(null);
              return;
            }
            const found = farms.rows.find((f) => String(f.id) === id);
            if (found) setActor({ id: found.id, name: found.name ?? `#${found.id}` });
          }}
        />

        {needArea ? (
          <NumberField label={t(m.fieldArea)} unit={t(m.haUnit)} value={area} onChange={setArea} minValue={0} fractionDigits={6} />
        ) : null}

        {form.kind === "change" ? (
          <>
            <DatePicker label={t(m.fieldFrom)} value={effectiveDay} onChange={setEffectiveOn} maxValue={slice ?? undefined} />
            <Checkbox isSelected={endOnly} onChange={setEndOnly}>
              {t(m.endOnly)}
            </Checkbox>
          </>
        ) : null}

        {error ? (
          <p role="alert" className="pl-error">
            {error}
          </p>
        ) : null}

        <DrawerFormActions
          sticky
          note={problem}
          primary={{ label: t(m.save), onPress: () => void submit(), isPending: busy, isDisabled: problem !== null }}
          cancel={t(COMMON.cancel)}
        />
      </div>
    </DetailDrawer>
  );
}

/* ------------------------------------------------------------------ импорт */

export function ImportDrawer({
  season,
  seasons,
  onDone,
  onClose,
}: {
  season: string;
  seasons: readonly string[];
  onDone: () => void;
  onClose: () => void;
}) {
  const t = useT();
  const analysis = useAnalysisContext();
  const [file, setFile] = useState<File | null>(null);
  const [adapter, setAdapter] = useState("canon_v1");
  const [fileSeason, setFileSeason] = useState(season);
  const [detail, setDetail] = useState<ImportDetail | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const seasonOptions = useMemo<Choice[]>(() => {
    const all = new Set([...seasons, season].filter(Boolean));
    return [...all].sort().reverse().map((s) => ({ id: s, name: s }));
  }, [seasons, season]);

  async function stage() {
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      const batch = await placementApi.upload(file, adapter, fileSeason.trim(), analysis.params);
      setDetail(await placementApi.importDetail(batch.id, analysis.params));
    } catch (cause) {
      setError(errorText(cause));
    } finally {
      setBusy(false);
    }
  }

  async function commit() {
    if (!detail) return;
    setBusy(true);
    setError(null);
    try {
      const batch = await placementApi.commit(detail.batch.id, analysis.params);
      const counts = batch.counts as Record<string, number>;
      toast.show({ message: t(m.importDone, { done: counts.committed ?? 0, failed: counts.failed ?? 0 }) });
      onDone();
      onClose();
    } catch (cause) {
      setError(errorText(cause));
    } finally {
      setBusy(false);
    }
  }

  const counts = (detail?.batch.counts ?? {}) as Record<string, number>;
  const toCommit = (counts.new ?? 0) + (counts.correction ?? 0);
  const problems = detail?.rows.filter((r) => !COMMITTABLE.has(r.status) && r.status !== "unchanged") ?? [];

  return (
    <DetailDrawer title={t(m.importTitle)} onClose={onClose}>
      <div className="pl-form" data-ai-region="placement-import">
        <p className="pl-hint">{t(m.importHint, { code: (chunks) => <code>{chunks}</code> })}</p>
        <FilePick
          label={t(m.importFile)}
          value={file}
          accept={[".csv", "text/csv"]}
          onChange={(next) => {
            setFile(next);
            setDetail(null);
            setError(null);
          }}
        />
        <Select
          label={t(m.importFormat)}
          value={adapter}
          onChange={(v) => setAdapter(v || "canon_v1")}
          options={[
            { id: "canon_v1", name: t(m.formatCanon) },
            { id: "table_csv", name: t(m.formatTable) },
          ]}
        />
        <Select label={t(m.importSeason)} value={fileSeason} onChange={(v) => setFileSeason(v)} options={seasonOptions} />
        <div>
          <button type="button" className="btn btn--secondary focus-ring" disabled={!file || busy} onClick={() => void stage()}>
            {t(m.importPreview)}
          </button>
        </div>

        {error ? (
          <p role="alert" className="pl-error">
            {error}
          </p>
        ) : null}

        {detail ? (
          <div className="pl-preview">
            <ul className="pl-counts">
              {IMPORT_STATUS_ORDER.map((status) =>
                counts[status] ? (
                  <li key={status} data-status={status}>
                    {t(m.countLine, { label: importStatusLabel(status), count: counts[status], b: (chunks) => <strong>{chunks}</strong> })}
                  </li>
                ) : null,
              )}
            </ul>
            {counts.farm_unmatched ? <p className="pl-hint">{t(m.farmUnmatched, { count: counts.farm_unmatched })}</p> : null}
            {problems.length > 0 ? (
              <section className="pl-problems" aria-label={t(m.problemsTitle)}>
                <h3 className="pl-problems__title">{t(m.problemsTitle)}</h3>
                <ul>
                  {problems.slice(0, 30).map((row) => (
                    <li key={row.row_no}>
                      <span className="pl-problems__head">
                        {t(m.problemRow, { row: row.row_no, contour: row.contour_raw || "—" })} <Badge tone="outline">{importStatusLabel(row.status)}</Badge>
                      </span>
                      <span className="pl-hint">{row.issues.map((i) => String(i.message)).join("; ")}</span>
                    </li>
                  ))}
                </ul>
                {problems.length > 30 ? <p className="pl-hint">{t(m.problemsMore, { count: problems.length - 30 })}</p> : null}
              </section>
            ) : null}
            <FormActions
              sticky
              note={toCommit === 0 ? t(m.commitNote) : null}
              primary={{ label: t(m.commitRows, { count: toCommit }), onPress: () => void commit(), isPending: busy, isDisabled: toCommit === 0 }}
              secondary={{
                label: t(m.rejectFile),
                onPress: () => {
                  void placementApi.reject(detail.batch.id, analysis.params);
                  setDetail(null);
                },
              }}
            />
          </div>
        ) : null}
      </div>
    </DetailDrawer>
  );
}

/* --------------------------------------------------------------- севооборот */

export function CropTimelineDrawer({ row, params, linkSearch, onClose }: { row: PlacementRow; params: QueryParams; linkSearch: string; onClose: () => void }) {
  const t = useT();
  const contour = useRequest(() => api.contour(row.contour_id, params), [row.contour_id, JSON.stringify(params)]);
  const blocks = contour.data?.crop_timeline ?? [];
  return (
    <DetailDrawer title={t(m.timelineTitle, { no: row.contour_no })} onClose={onClose}>
      <div className="pl-form">
        {failed(contour) ? (
          <LoadError compact what={t(m.timelineLoadWhat)} error={contour.error!} onRetry={contour.reload} />
        ) : !contour.data ? (
          <SkeletonList label={t(m.timelineLoadWhat)} items={3} />
        ) : blocks.length === 0 ? (
          <EmptyState compact title={t(m.timelineEmptyTitle)} reason={t(m.timelineEmptyReason)} />
        ) : (
          <DemoCropTimeline blocks={blocks} axis="contour" linkSearch={linkSearch} />
        )}
        <Link className="linkish" to={{ pathname: `/contours/${row.contour_id}`, search: linkSearch }}>
          {t(m.timelineCard)}
        </Link>
      </div>
    </DetailDrawer>
  );
}
